/**
 * NATEX OPPORTUNITY SCORE - TYPE DEFINITIONS
 *
 * Professional-grade, institutional-quality type system for the
 * proprietary investment attractiveness index.
 *
 * Methodology inspired by:
 * - Moody's/S&P/Fitch credit rating pillars
 * - MSCI ESG Government Ratings
 * - Kearney FDI Confidence Index
 */

// ============================================================================
// ENUMS
// ============================================================================

export enum Pillar {
  GROWTH_DRIVERS = 'GROWTH_DRIVERS',
  INVESTMENT_CLIMATE = 'INVESTMENT_CLIMATE',
  CATALYST_PIPELINE = 'CATALYST_PIPELINE',
  RISK_PROFILE = 'RISK_PROFILE',
  VALUATION_SIGNAL = 'VALUATION_SIGNAL'
}

export enum Factor {
  // Growth Drivers
  ECONOMIC_MOMENTUM = 'ECONOMIC_MOMENTUM',
  DEMOGRAPHIC_DIVIDEND = 'DEMOGRAPHIC_DIVIDEND',
  DIGITAL_READINESS = 'DIGITAL_READINESS',
  // Investment Climate
  POLICY_ENVIRONMENT = 'POLICY_ENVIRONMENT',
  MARKET_ACCESS = 'MARKET_ACCESS',
  EASE_OF_OPERATIONS = 'EASE_OF_OPERATIONS',
  // Catalyst Pipeline
  REFORM_MOMENTUM = 'REFORM_MOMENTUM',
  FDI_PIPELINE = 'FDI_PIPELINE',
  EVENT_CALENDAR = 'EVENT_CALENDAR',
  // Risk Profile
  POLITICAL_STABILITY = 'POLITICAL_STABILITY',
  MACRO_STABILITY = 'MACRO_STABILITY',
  EXTERNAL_VULNERABILITY = 'EXTERNAL_VULNERABILITY',
  // Valuation Signal
  RELATIVE_VALUATION = 'RELATIVE_VALUATION',
  TIMING_INDICATOR = 'TIMING_INDICATOR'
}

export enum DataSource {
  WORLD_BANK = 'WORLD_BANK',
  IMF = 'IMF',
  UN_POPULATION = 'UN_POPULATION',
  ITU = 'ITU',
  WGI = 'WGI',           // World Governance Indicators
  B_READY = 'B_READY',   // World Bank Business Ready
  LPI = 'LPI',           // Logistics Performance Index
  IEP = 'IEP',           // Institute for Economics & Peace
  NATEX = 'NATEX'        // Proprietary tracking
}

export enum ScoreSignal {
  STRONG_BUY = 'STRONG_BUY',
  BUY = 'BUY',
  HOLD = 'HOLD',
  UNDERWEIGHT = 'UNDERWEIGHT',
  AVOID = 'AVOID'
}

export enum TrendDirection {
  RISING = 'RISING',
  STABLE = 'STABLE',
  FALLING = 'FALLING'
}

// ============================================================================
// INDICATOR DEFINITIONS
// ============================================================================

export interface ScoringThreshold {
  minValue: number;
  maxValue: number;
  score: number;
  label?: string;
}

export interface Indicator {
  id: string;
  name: string;
  description: string;
  pillar: Pillar;
  factor: Factor;
  weight: number;
  source: DataSource;
  sourceCode?: string;
  unit: string;
  isHigherBetter: boolean;
  thresholds: ScoringThreshold[];
  updateFrequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual';
  lastUpdated?: Date;
}

// ============================================================================
// SCORE STRUCTURES
// ============================================================================

export interface IndicatorScore {
  indicatorId: string;
  rawValue: number | null;
  score: number;
  weightedScore: number;
  source: DataSource;
  asOfDate: Date;
  isEstimated: boolean;
}

export interface FactorScore {
  factor: Factor;
  name: string;
  score: number;
  weight: number;
  weightedScore: number;
  indicators: IndicatorScore[];
  dataCompleteness: number;
}

export interface PillarScore {
  pillar: Pillar;
  name: string;
  score: number;
  weight: number;
  contribution: number;
  factors: FactorScore[];
  trend: TrendDirection;
  changeFromPrevious: number;
  icon: string;
  color: string;
}

export interface ScoreAdjustment {
  id: string;
  type: 'EXCEPTIONAL_REFORM' | 'CRISIS' | 'RATING_ACTION' | 'MAJOR_DEAL' | 'GEOPOLITICAL' | 'OTHER';
  description: string;
  impact: number;
  validFrom: Date;
  validTo?: Date;
  source: string;
}

export interface KeyDriver {
  indicator: string;
  indicatorId: string;
  description: string;
  impact: number;
  isPositive: boolean;
  category: 'strength' | 'weakness' | 'catalyst' | 'risk';
  pillar: Pillar;
  rawValue: number | null;
  unit: string;
}

export interface NatexScore {
  // Core score
  countryCode: string;
  countryName: string;
  score: number;
  baseScore: number;
  signal: ScoreSignal;
  stars: 1 | 2 | 3 | 4 | 5;
  grade: string;

  // Breakdown
  pillars: PillarScore[];
  adjustments: ScoreAdjustment[];

  // Analysis
  keyDrivers: KeyDriver[];
  strengths: string[];
  weaknesses: string[];

  // Trends
  trend: TrendDirection;
  previousScore: number;
  scoreChange: number;

  // Metadata
  calculatedAt: Date;
  dataAsOf: Date;
  dataCompleteness: number;
  methodology: string;
}

// ============================================================================
// PEER COMPARISON
// ============================================================================

export interface PeerComparison {
  countryCode: string;
  countryName: string;
  score: number;
  signal: ScoreSignal;
  stars: 1 | 2 | 3 | 4 | 5;
  rank: number;
  pillarScores: Record<Pillar, number>;
  vsTarget: number;
  trend: TrendDirection;
}

export interface ScoreHistory {
  countryCode: string;
  period: string;
  score: number;
  signal: ScoreSignal;
  keyEvent?: string;
}

// ============================================================================
// COUNTRY DATA INPUT
// ============================================================================

export interface CountryIndicatorData {
  countryCode: string;
  countryName: string;
  indicators: Record<string, number | null>;
  asOfDate: Date;
}

export interface CountryScoreInput {
  countryData: CountryIndicatorData;
  adjustments?: ScoreAdjustment[];
  previousScore?: number;
}

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================

export const PILLAR_WEIGHTS: Record<Pillar, number> = {
  [Pillar.GROWTH_DRIVERS]: 0.30,
  [Pillar.INVESTMENT_CLIMATE]: 0.25,
  [Pillar.CATALYST_PIPELINE]: 0.20,
  [Pillar.RISK_PROFILE]: 0.15,
  [Pillar.VALUATION_SIGNAL]: 0.10
};

export const PILLAR_CONFIG: Record<Pillar, { name: string; icon: string; color: string }> = {
  [Pillar.GROWTH_DRIVERS]: { name: 'Growth Drivers', icon: 'TrendingUp', color: 'data-green' },
  [Pillar.INVESTMENT_CLIMATE]: { name: 'Investment Climate', icon: 'Building', color: 'data-blue' },
  [Pillar.CATALYST_PIPELINE]: { name: 'Catalyst Pipeline', icon: 'Rocket', color: 'data-amber' },
  [Pillar.RISK_PROFILE]: { name: 'Risk Profile', icon: 'Shield', color: 'data-red' },
  [Pillar.VALUATION_SIGNAL]: { name: 'Valuation Signal', icon: 'Target', color: 'data-purple' }
};

export const FACTOR_WEIGHTS: Record<Factor, number> = {
  // Growth Drivers (30% total)
  [Factor.ECONOMIC_MOMENTUM]: 0.333,
  [Factor.DEMOGRAPHIC_DIVIDEND]: 0.333,
  [Factor.DIGITAL_READINESS]: 0.334,
  // Investment Climate (25% total)
  [Factor.POLICY_ENVIRONMENT]: 0.40,
  [Factor.MARKET_ACCESS]: 0.32,
  [Factor.EASE_OF_OPERATIONS]: 0.28,
  // Catalyst Pipeline (20% total)
  [Factor.REFORM_MOMENTUM]: 0.40,
  [Factor.FDI_PIPELINE]: 0.35,
  [Factor.EVENT_CALENDAR]: 0.25,
  // Risk Profile (15% total)
  [Factor.POLITICAL_STABILITY]: 0.333,
  [Factor.MACRO_STABILITY]: 0.333,
  [Factor.EXTERNAL_VULNERABILITY]: 0.334,
  // Valuation Signal (10% total)
  [Factor.RELATIVE_VALUATION]: 0.50,
  [Factor.TIMING_INDICATOR]: 0.50
};

export const SCORE_THRESHOLDS: Record<ScoreSignal, { min: number; max: number; stars: 1 | 2 | 3 | 4 | 5 }> = {
  [ScoreSignal.STRONG_BUY]: { min: 85, max: 100, stars: 5 },
  [ScoreSignal.BUY]: { min: 70, max: 84, stars: 4 },
  [ScoreSignal.HOLD]: { min: 55, max: 69, stars: 3 },
  [ScoreSignal.UNDERWEIGHT]: { min: 40, max: 54, stars: 2 },
  [ScoreSignal.AVOID]: { min: 0, max: 39, stars: 1 }
};

export const SCORE_GRADES: { min: number; max: number; grade: string }[] = [
  { min: 95, max: 100, grade: 'A+' },
  { min: 90, max: 94, grade: 'A' },
  { min: 85, max: 89, grade: 'A-' },
  { min: 80, max: 84, grade: 'B+' },
  { min: 75, max: 79, grade: 'B' },
  { min: 70, max: 74, grade: 'B-' },
  { min: 65, max: 69, grade: 'C+' },
  { min: 60, max: 64, grade: 'C' },
  { min: 55, max: 59, grade: 'C-' },
  { min: 45, max: 54, grade: 'D' },
  { min: 0, max: 44, grade: 'F' }
];

export const PILLAR_FACTORS: Record<Pillar, Factor[]> = {
  [Pillar.GROWTH_DRIVERS]: [
    Factor.ECONOMIC_MOMENTUM,
    Factor.DEMOGRAPHIC_DIVIDEND,
    Factor.DIGITAL_READINESS
  ],
  [Pillar.INVESTMENT_CLIMATE]: [
    Factor.POLICY_ENVIRONMENT,
    Factor.MARKET_ACCESS,
    Factor.EASE_OF_OPERATIONS
  ],
  [Pillar.CATALYST_PIPELINE]: [
    Factor.REFORM_MOMENTUM,
    Factor.FDI_PIPELINE,
    Factor.EVENT_CALENDAR
  ],
  [Pillar.RISK_PROFILE]: [
    Factor.POLITICAL_STABILITY,
    Factor.MACRO_STABILITY,
    Factor.EXTERNAL_VULNERABILITY
  ],
  [Pillar.VALUATION_SIGNAL]: [
    Factor.RELATIVE_VALUATION,
    Factor.TIMING_INDICATOR
  ]
};

export const FACTOR_NAMES: Record<Factor, string> = {
  [Factor.ECONOMIC_MOMENTUM]: 'Economic Momentum',
  [Factor.DEMOGRAPHIC_DIVIDEND]: 'Demographic Dividend',
  [Factor.DIGITAL_READINESS]: 'Digital Readiness',
  [Factor.POLICY_ENVIRONMENT]: 'Policy Environment',
  [Factor.MARKET_ACCESS]: 'Market Access',
  [Factor.EASE_OF_OPERATIONS]: 'Ease of Operations',
  [Factor.REFORM_MOMENTUM]: 'Reform Momentum',
  [Factor.FDI_PIPELINE]: 'FDI Pipeline',
  [Factor.EVENT_CALENDAR]: 'Event Calendar',
  [Factor.POLITICAL_STABILITY]: 'Political Stability',
  [Factor.MACRO_STABILITY]: 'Macro Stability',
  [Factor.EXTERNAL_VULNERABILITY]: 'External Vulnerability',
  [Factor.RELATIVE_VALUATION]: 'Relative Valuation',
  [Factor.TIMING_INDICATOR]: 'Timing Indicator'
};

// Central Asia & Silk Road Corridor peer groups
export const PEER_GROUPS = {
  central_asia: ['UZB', 'KAZ', 'KGZ', 'TJK', 'TKM'],
  silk_road_corridor: ['UZB', 'KAZ', 'KGZ', 'TJK', 'TKM', 'AZE', 'GEO', 'MNG'],
  caucasus: ['AZE', 'GEO'],
  hydrocarbon_economies: ['UZB', 'KAZ', 'TKM', 'AZE'],
  reform_leaders: ['UZB', 'KAZ', 'GEO', 'AZE', 'MNG']
} as const;

export type PeerGroup = keyof typeof PEER_GROUPS;
